import parse from 'html-react-parser';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { GlassBox } from 'components/glassBox/GlassBox';

import { ReactComponent as InfoIcon } from 'assets/icons/info-filled.svg';

import styles from './Tooltip.module.scss';

// Constants
const TOOLTIP_OFFSET = {
  TOP: 20,
  BOTTOM: 14,
  HORIZONTAL: 14,
} as const;

const Z_INDEX = 101;
const DEFAULT_CONTENT = '-';
const VIEWPORT_MARGIN = 8;

// Types
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
type TooltipVariant = 'default' | 'glass';

interface ITooltipProps {
  content?: ReactNode;
  children?: ReactNode;
  text?: string;
  className?: string;
  iconClassName?: string;
  contentClassName?: string;
  position?: TooltipPosition;
  visible?: boolean;
  wrapperClassName?: string;
  variant?: TooltipVariant;
  glassGradient?: string;
  offset?: number;
}

interface IPosition {
  top: number;
  left: number;
}

interface IRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

// Utility functions
const calculateTooltipPosition = (
  triggerRect: IRect,
  contentWidth: number,
  contentHeight: number,
  position: TooltipPosition,
  customOffset?: number
): IPosition => {
  const horizontalOffset = customOffset ?? TOOLTIP_OFFSET.HORIZONTAL;
  const positionCalculators = {
    top: () => ({
      top: triggerRect.top - contentHeight - TOOLTIP_OFFSET.TOP,
      left: triggerRect.left + triggerRect.width / 2,
    }),
    bottom: () => ({
      top: triggerRect.bottom + TOOLTIP_OFFSET.BOTTOM,
      left: triggerRect.left + triggerRect.width / 2,
    }),
    left: () => ({
      top: triggerRect.top + triggerRect.height / 2,
      left: triggerRect.left - contentWidth - horizontalOffset,
    }),
    right: () => ({
      top: triggerRect.top + triggerRect.height / 2,
      left: triggerRect.right + horizontalOffset,
    }),
  };

  return positionCalculators[position]();
};

/**
 * Keep a fixed-position tooltip inside the viewport. The trigger can sit near a
 * screen edge (common on mobile), which would otherwise push a centered tooltip
 * off-screen. Accounts for the centering transform of each position.
 */
const clampToViewport = (
  { top, left }: IPosition,
  contentWidth: number,
  contentHeight: number,
  position: TooltipPosition
): IPosition => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  if (!vw || !vh) return { top, left };

  const clampCentered = (value: number, size: number, viewport: number) => {
    const half = size / 2;
    const min = VIEWPORT_MARGIN + half;
    const max = viewport - VIEWPORT_MARGIN - half;
    return max < min ? viewport / 2 : Math.min(Math.max(value, min), max);
  };
  const clampEdge = (value: number, size: number, viewport: number) =>
    Math.min(Math.max(value, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, viewport - VIEWPORT_MARGIN - size));

  const horizontalCentered = position === 'top' || position === 'bottom';
  const verticalCentered = position === 'left' || position === 'right';

  return {
    left:
      contentWidth > 0
        ? horizontalCentered
          ? clampCentered(left, contentWidth, vw)
          : clampEdge(left, contentWidth, vw)
        : left,
    top:
      contentHeight > 0
        ? verticalCentered
          ? clampCentered(top, contentHeight, vh)
          : clampEdge(top, contentHeight, vh)
        : top,
  };
};

const getTransformStyle = (position: TooltipPosition): string => {
  const horizontalPositions: TooltipPosition[] = ['left', 'right'];
  return horizontalPositions.includes(position) ? 'translateX(0) translateY(-50%)' : 'translateX(-50%)';
};

const getTooltipContent = (content: ReactNode | undefined, text: string | undefined): ReactNode => {
  if (content) return content;
  if (text) return parse(text);
  return DEFAULT_CONTENT;
};

const hasColorClass = (className: string | undefined): boolean => {
  if (!className) return false;
  const colorPatterns = [/tw-text-/, /text-/];
  return colorPatterns.some((pattern) => pattern.test(className));
};

const getIconClassName = (iconClassName: string | undefined): string => {
  const baseClasses = 'tw-w-5';
  const colorClass = hasColorClass(iconClassName) ? '' : 'tw-text-skyBlue';
  return twMerge(baseClasses, colorClass, iconClassName);
};

// TooltipArrow component
const TooltipArrow = ({ top, position }: { top: number; position: TooltipPosition }) => {
  if (position !== 'right') return null;

  return <div className={styles['tooltip-arrow']} style={{ top: `${top}px` }} />;
};

// Main component
const Tooltip = ({
  content,
  children,
  text,
  className,
  iconClassName,
  contentClassName,
  position = 'top',
  visible = true,
  wrapperClassName,
  variant = 'default',
  glassGradient = 'linear-gradient(to right, #2D5EFF 0%, #389FFF 100%)',
  offset,
}: ITooltipProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<IPosition>({ top: 0, left: 0 });
  const [arrowTop, setArrowTop] = useState<number>(0);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  useEffect(() => {
    if (!isHovered || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current?.getBoundingClientRect();
    const contentWidth = contentRect?.width ?? 0;
    const contentHeight = contentRect?.height ?? 0;

    const rawPosition = calculateTooltipPosition(triggerRect, contentWidth, contentHeight, position, offset);
    setTooltipPosition(clampToViewport(rawPosition, contentWidth, contentHeight, position));

    // Calculate arrow position: center of tooltip minus half of arrow height (8.5px)
    if (contentHeight > 0) {
      const arrowHeight = 17; // Total arrow height
      const calculatedTop = contentHeight / 2 - arrowHeight / 2;
      setArrowTop(calculatedTop);
    }
  }, [isHovered, position, offset]);

  const shouldRenderTooltip = isHovered && visible;

  const renderTooltipContent = () => {
    const tooltipText = getTooltipContent(content, text);
    const baseClassName = twMerge(
      'tw-visible tw-fixed tw-opacity-100',
      `tw-z-[${Z_INDEX}]`,
      contentClassName,
      variant === 'glass' ? styles['tooltip-content--glass'] : styles['tooltip-content'],
      styles[`tooltip-content--${position}`]
    );

    const baseStyle = {
      top: `${tooltipPosition.top}px`,
      left: `${tooltipPosition.left}px`,
      position: 'fixed' as const,
      transform: getTransformStyle(position),
    };

    if (variant === 'glass') {
      return (
        <div ref={contentRef} className={baseClassName} style={baseStyle}>
          <TooltipArrow top={arrowTop} position={position} />
          <GlassBox
            gradient={glassGradient}
            backgroundOpacity={1}
            borderWidth="1px"
            className="tw-p-[15px_20px_15px_20px]"
          >
            <div className="tw-text-white">{tooltipText}</div>
          </GlassBox>
        </div>
      );
    }

    return (
      <div ref={contentRef} className={baseClassName} style={baseStyle}>
        {tooltipText}
      </div>
    );
  };

  const tooltipContent = shouldRenderTooltip ? createPortal(renderTooltipContent(), document.body) : null;

  if (!visible) {
    return <>{children}</>;
  }

  return (
    <>
      <div className={twMerge(className, styles['tooltip'])}>
        <div
          ref={triggerRef}
          className={twMerge(
            wrapperClassName,
            'tw-relative tw-inline-block',
            children ? 'tw-h-auto' : 'tw-h-5'
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children || <InfoIcon className={getIconClassName(iconClassName)} />}
        </div>
      </div>
      {tooltipContent}
    </>
  );
};

export default Tooltip;
